import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useIsFetching } from '@tanstack/react-query';
import {
  BellIcon,
  DownloadIcon,
  GiftIcon,
  ImageIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MenuIcon,
  MessageSquareTextIcon,
  PackageCheckIcon,
  ShieldCheckIcon,
  SirenIcon,
  TicketIcon,
  TrophyIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { AdminRole } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ThemeToggle } from '@/components/theme-toggle';
import { authStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { strings } from '@/i18n/strings';

/**
 * Yon paneldagi bo'limlar, ikki guruhda.
 *
 * Guruhlash ataylab ish oqimi bo'yicha: yuqoridagi uchtasi — ertalab va efir kunida ochiladigan
 * ekranlar, pastdagilar esa vaqti-vaqti bilan kerak bo'ladigan yuzalar. Ro'yxat tartibi
 * `strings.nav` kalitlari tartibi bilan bir xil (spec test buni ushlab turadi).
 */
const NAV_GROUPS = [
  {
    label: strings.shell.groupOperations,
    items: [
      { to: '/', label: strings.nav.dashboard, icon: LayoutDashboardIcon },
      { to: '/draws', label: strings.nav.draws, icon: TrophyIcon },
      { to: '/moderation', label: strings.nav.moderation, icon: ShieldCheckIcon },
      { to: '/dlq', label: strings.nav.dlq, icon: SirenIcon },
      { to: '/notifications', label: strings.nav.notifications, icon: BellIcon },
    ],
  },
  {
    label: strings.shell.groupSystem,
    items: [
      { to: '/users', label: strings.nav.users, icon: UsersIcon },
      { to: '/export', label: strings.nav.exportData, icon: DownloadIcon },
      { to: '/accounts', label: strings.nav.accounts, icon: TicketIcon },
      { to: '/rewards', label: strings.nav.rewards, icon: GiftIcon },
      { to: '/reward-redemptions', label: strings.nav.redemptions, icon: PackageCheckIcon },
      { to: '/banners', label: strings.nav.banners, icon: ImageIcon },
      {
        to: '/notification-templates',
        label: strings.nav.notificationTemplates,
        icon: MessageSquareTextIcon,
      },
    ],
  },
] as const;

/**
 * Faol bo'lim uchun qo'shimcha klasslar.
 *
 * `!text-…` — faol klass oddiy klass bilan bir xil xususiyatga ega, ya'ni g'olibni CSS dagi
 * tartib hal qilardi; `important` uni aniq qiladi. `before:*` esa chap chekkadagi indikator
 * chizig'ini ochadi: u har bir bo'limda MAVJUD, faqat faol bo'lmaganda balandligi nolga
 * siqilgan — shu sababdan u paydo bo'lmaydi, o'sadi, va bo'lim almashganda ko'z uni bir
 * joydan ikkinchisiga o'tgan deb o'qiydi.
 */
const NAV_ACTIVE_CLASS =
  'bg-sidebar-accent !text-sidebar-accent-foreground shadow-xs before:scale-y-100 before:opacity-100 [&_svg]:text-sidebar-primary';

export function AdminShell() {
  const navigate = useNavigate();
  const session = authStore.get();
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /**
   * Fon so'rovlari.
   *
   * Panel ma'lumotni oyna fokusga qaytganda va vaqti-vaqti bilan o'zi qayta so'raydi. Bunday
   * yangilanish ekranda HECH QANDAY iz qoldirmasdi: jadval bir zumda boshqa sonlar bilan
   * qayta chizilardi va operator buni "men nimanidir bosdimmi" deb o'qirdi. Yuqoridagi
   * ingichka chiziq — javob shu.
   */
  const fetching = useIsFetching();

  /**
   * Sarlavha satri sahifa aylantirilgandan keyingina chegara oladi.
   *
   * Doim turgan chegara sahifa tepasida keraksiz chiziq bo'lib turadi; umuman bo'lmagani esa
   * jadval sarlavha ostidan o'tib ketayotganda uni mazmun bilan qo'shib yuboradi.
   */
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Bo'lim almashganda mobil menyu o'zi yopiladi — aks holda u tanlangan sahifani to'sib turardi.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const logout = () => {
    authStore.clear();
    void navigate({ to: '/login' });
  };

  return (
    <div className="app-canvas bg-background min-h-screen">
      {/*
        Fon so'rovi ko'rsatkichi — eng tepada, butun kenglik bo'ylab.

        U ANIQ EMAS (nechi foiz bajarilgani noma'lum), shuning uchun to'ldiriladigan chiziq
        emas, o'ngga yuguradigan yorug'lik. Ekran o'quvchisidan yashirilgan: bu yerda amal yo'q,
        va "yuklanmoqda" ni har o'ttiz soniyada e'lon qilish ovozli navigatsiyani buzardi.
      */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none fixed inset-x-0 top-0 z-60 h-0.5 overflow-hidden transition-opacity duration-(--dur-control)',
          fetching > 0 ? 'opacity-100' : 'opacity-0',
        )}
      >
        <div className="via-primary h-full w-1/3 bg-gradient-to-r from-transparent to-transparent [animation:shell-progress_1.1s_var(--ease-standard)_infinite]" />
      </div>

      {/*
        Yon panel BITTA marta chiziladi va kichik ekranda ekrandan tashqariga suriladi. Ikkita
        nusxa (mobil + desktop) har bir havolani ikki marta beradi, ya'ni klaviatura bilan yurish
        ham, avtomatik testlar ham ikkilanadigan bo'lardi.
      */}
      <aside
        className={cn(
          'bg-sidebar text-sidebar-foreground border-sidebar-border bg-(image:--sidebar-surface) fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r transition-transform duration-300 ease-(--ease-out-soft) lg:translate-x-0',
          menuOpen ? 'translate-x-0 shadow-(--shadow-3)' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 px-4">
          <span
            aria-hidden="true"
            className="from-sidebar-primary to-sidebar-primary/75 text-sidebar-primary-foreground shadow-[0_2px_12px_-2px_var(--sidebar-primary)] flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-sm font-black"
          >
            Q
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm leading-tight font-semibold">
              {strings.app.title}
            </span>
            <span className="text-sidebar-foreground/60 block truncate text-xs leading-tight">
              {strings.shell.subtitle}
            </span>
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ml-auto lg:hidden"
            aria-label={strings.shell.closeMenu}
            onClick={() => setMenuOpen(false)}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </div>

        <Separator className="bg-sidebar-border" />

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4" aria-label={strings.app.title}>
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="motion-stagger space-y-1">
              <p className="text-sidebar-foreground/45 px-3 pb-1 text-[11px] font-semibold tracking-wider uppercase">
                {group.label}
              </p>
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  // `activeProps` — TanStack Router ning o'z faol holati; qo'lda `pathname`
                  // solishtirish ichma-ich marshrutlarda (`/draws/$drawId`) noto'g'ri javob berardi.
                  activeOptions={{ exact: item.to === '/' }}
                  className="text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-[color,background-color,transform] duration-(--dur-control) ease-(--ease-out-soft) before:absolute before:top-1/2 before:left-0 before:h-5 before:w-[3px] before:-translate-y-1/2 before:scale-y-0 before:rounded-r-full before:bg-(--sidebar-primary) before:opacity-0 before:transition-all before:duration-(--dur-control) before:ease-(--ease-spring) hover:translate-x-0.5 [&_svg]:transition-colors"
                  activeProps={{ className: NAV_ACTIVE_CLASS }}
                >
                  <item.icon className="size-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {session ? (
          <div className="border-sidebar-border flex items-center gap-2.5 border-t px-4 py-3">
            <span
              aria-hidden="true"
              className="bg-sidebar-accent text-sidebar-accent-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase"
            >
              {session.admin.email.slice(0, 2)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{session.admin.email}</span>
              <Badge
                variant="outline"
                className="border-sidebar-border text-sidebar-foreground/70 mt-1"
              >
                {session.admin.role === AdminRole.ADMIN
                  ? strings.accounts.roleAdmin
                  : strings.accounts.roleOperator}
              </Badge>
            </span>
          </div>
        ) : null}
      </aside>

      {menuOpen ? (
        <div
          className="bg-foreground/40 motion-fade fixed inset-0 z-40 backdrop-blur-[2px] lg:hidden"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <div className="lg:pl-64">
        <header
          className={cn(
            'bg-background/70 sticky top-0 z-30 flex h-14 items-center gap-2 border-b px-4 backdrop-blur-md transition-[border-color,box-shadow] duration-(--dur-control) lg:px-8',
            scrolled ? 'shadow-(--shadow-1)' : 'border-transparent',
          )}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label={strings.shell.openMenu}
            onClick={() => setMenuOpen(true)}
          >
            <MenuIcon aria-hidden="true" />
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOutIcon aria-hidden="true" />
              {strings.nav.logout}
            </Button>
          </div>
        </header>

        {/*
          `key` — manzilning o'zi: bo'lim almashganda ish maydoni qayta chiziladi va kirish
          animatsiyasi qaytadan o'ynaydi. Bu shunchaki bezak emas — panelning ekranlari
          bir-biriga o'xshash (sarlavha + jadval), va harakatsiz almashuvda operator sahifa
          umuman o'zgarganini payqamay qolardi.
        */}
        <main
          key={pathname}
          className="motion-page mx-auto w-full max-w-[110rem] px-4 py-6 lg:px-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
